import { Link } from "react-router-dom";
import { Check, X, ArrowRight } from "lucide-react";
import { useLang } from "@/contexts/LangContext";
import Reveal from "@/components/Reveal";
import GoldCorners from "@/components/GoldCorners";

const HERO =
  "https://customer-assets.emergentagent.com/job_web-essentials-21/artifacts/jbmbbih9_IMG_2329.png";
const SECONDARY = HERO;

export default function SpaAtHome() {
  const { t } = useLang();

  return (
    <div data-testid="page-spa" className="bg-[#070E13] text-[#F4EEE8]">
      {/* HERO with right-anchored signature image */}
      <section className="relative bg-[#070E13] text-[#F4EEE8] pt-40 pb-32 overflow-hidden">
        <div className="absolute inset-0 lg:inset-y-0 lg:right-0 lg:left-[42%]">
          <img src={HERO} alt="Spa at home" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#070E13] from-0% via-[#070E13]/80 via-25% to-transparent to-55%" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#070E13]/35 via-transparent to-[#070E13]/55" />
          <div className="absolute inset-0 mix-blend-overlay opacity-25" style={{ background: "linear-gradient(135deg, rgba(199,162,108,0.20), rgba(7,14,19,0.4))" }} />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 sm:px-10 lg:px-12">
          <div className="lg:w-[55%]">
            <Reveal>
              <p className="overline gold-text">{t.spa.eyebrow}</p>
              <h1 className="h-editorial text-5xl sm:text-6xl lg:text-7xl mt-6 max-w-5xl">{t.spa.title}</h1>
              <p className="mt-8 max-w-2xl text-lg text-[#D6D1C9]">{t.spa.lead}</p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="bg-[#070E13] py-24 sm:py-32 border-t border-[#C7A26C]/15">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-12">
          <Reveal>
            <p className="overline gold-text">{t.spa.howTitle}</p>
            <h2 className="h-editorial text-4xl sm:text-5xl mt-5 text-[#F4EEE8] max-w-3xl">
              {t.spa.title}
            </h2>
          </Reveal>
          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            {t.spa.steps.map((s, i) => (
              <Reveal key={i} delay={i * 80} className="relative bg-[#0C1821] text-[#F4EEE8] p-8 lg:p-9 min-h-[280px] flex flex-col border border-[#C7A26C]/20 hover:border-[#C7A26C]/55 hover:bg-[#14222C] transition-colors duration-500">
                <GoldCorners size={10} inset={-1} />
                <p className="font-serif text-5xl gold-text">{s.n}</p>
                <h3 className="font-serif text-2xl mt-6">{s.t}</h3>
                <p className="mt-4 text-sm text-[#D6D1C9] leading-relaxed">{s.d}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* WHY IT WORKS */}
      <section className="bg-[#070E13] text-[#F4EEE8] py-24 sm:py-32 relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-12">
          <Reveal>
            <p className="overline text-[#C7A26C]">{t.spa.whyTitle}</p>
          </Reveal>
          <div className="mt-12 grid grid-cols-1 lg:grid-cols-2 gap-px bg-white/10 border border-white/10">
            <div className="bg-[#070E13] p-10">
              <p className="font-serif text-2xl text-[#F4EEE8]/60 italic">{t.spa.compareLeft}</p>
              <ul className="mt-8 space-y-5">
                {t.spa.compare.map(([left], i) => (
                  <li key={i} className="flex gap-4 items-start text-[#F4EEE8]/70">
                    <X size={18} className="text-[#C77D66] mt-1 shrink-0" />
                    <span>{left}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-[#14222C] p-10 border-l border-[#C7A26C]/30">
              <p className="font-serif text-2xl text-[#C7A26C]">{t.spa.compareRight}</p>
              <ul className="mt-8 space-y-5">
                {t.spa.compare.map(([, right], i) => (
                  <li key={i} className="flex gap-4 items-start">
                    <Check size={18} className="text-[#2FB7B1] mt-1 shrink-0" />
                    <span>{right}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* DESIGNED FOR */}
      <section className="bg-[#070E13] py-24 sm:py-32 border-t border-[#C7A26C]/15">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <Reveal className="lg:col-span-6 relative">
            <div className="relative aspect-[4/5] overflow-hidden">
              <img src={SECONDARY} alt="Private wellness" className="absolute inset-0 w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#070E13]/60 via-transparent to-transparent" />
              <div className="absolute inset-0 mix-blend-overlay opacity-30" style={{ background: "linear-gradient(135deg, rgba(199,162,108,0.18), rgba(7,14,19,0.4))" }} />
              <GoldCorners size={14} inset={-2} />
            </div>
          </Reveal>
          <Reveal delay={150} className="lg:col-span-6">
            <p className="overline gold-text">{t.spa.designedFor}</p>
            <h2 className="h-editorial text-4xl sm:text-5xl mt-5 text-[#F4EEE8]">Reset · Rebuild · Sustain</h2>
            <ul className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-5 font-serif text-2xl text-[#F4EEE8]">
              {t.spa.designedForList.map((d, i) => (
                <li key={i} className="border-b border-[#C7A26C]/25 pb-3">{d}</li>
              ))}
            </ul>
            <Link
              to="/contact"
              data-testid="spa-bottom-cta"
              className="cta-premium inline-flex items-center gap-2 mt-12 px-9 py-4 bg-gradient-to-br from-[#E0C58E] via-[#C7A26C] to-[#A8845A] text-[#070E13] text-xs uppercase tracking-[0.24em] font-medium"
            >
              {t.common.bookConsultation} <ArrowRight size={16} />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
